import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, httpLink, splitLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import "./index.css";
import { getEmployeeToken, clearEmployeeToken } from "@/lib/employeeToken";

// Session token key for admin OAuth (Safari ITP workaround)
const SESSION_TOKEN_KEY = "darkhorse_session_token";

export function getSessionToken(): string | null {
  try {
    return localStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setSessionToken(token: string): void {
  try {
    localStorage.setItem(SESSION_TOKEN_KEY, token);
  } catch {
    console.warn("[Auth] Failed to save session token to localStorage");
  }
}

export function clearSessionToken(): void {
  try {
    localStorage.removeItem(SESSION_TOKEN_KEY);
  } catch {
    // ignore
  }
}

const queryClient = new QueryClient();

const handleUnauthorizedError = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;
  // The private review can be opened anonymously; financial matching asks for login inline.
  if (window.location.pathname === "/transaction-review") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  // Clear any stale localStorage tokens
  clearEmployeeToken();
  clearSessionToken();

  // Instead of redirecting to Manus OAuth, just reload the current page.
  // The DashboardLayout will detect the missing session and show the
  // custom LoginScreen (which includes email/password for employees).
  if (window.location.pathname !== "/") {
    window.location.href = "/";
  }
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    handleUnauthorizedError(error);
    if (window.location.pathname !== "/transaction-review") console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    handleUnauthorizedError(error);
    if (window.location.pathname !== "/transaction-review") console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    splitLink({
      condition: op => op.path.startsWith("transactionReview."),
      true: httpLink({url:"/api/trpc", transformer:superjson, methodOverride:"POST",
        headers() {
          const token = getEmployeeToken() || getSessionToken();
          return token ? {Authorization:`Bearer ${token}`} : {};
        },
        fetch(input,init) {
          return globalThis.fetch(input,{...(init ?? {}),credentials:"include",cache:"no-store",referrerPolicy:"no-referrer"});
        },
      }),
      false: httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      headers() {
        // Priority: employee token > session token (OAuth fallback)
        const empToken = getEmployeeToken();
        if (empToken) {
          return { Authorization: `Bearer ${empToken}` };
        }
        // Fallback: use session token from localStorage (Safari ITP workaround)
        const sessionToken = getSessionToken();
        if (sessionToken) {
          return { Authorization: `Bearer ${sessionToken}` };
        }
        return {};
      },
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
    }),
  ],
});

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);
